import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CreateTenantBodyDto,
  GalleryDto,
  InvitationDto,
  MemberDto,
  MemberRole,
  TenantDto,
  UpdateMemberBodyDto,
  UpdateTenantBodyDto,
  UserDto,
} from '@picflow/types';
import { lastValueFrom } from 'rxjs';
import { AuthService } from '../auth/services/auth.service';
import { membersCacheMaxAge } from '../consts';
import { BytesStringToKb } from '../lib/bytes-string-to-kb';
import { debounce } from '../lib/debounce';
import { randomString } from '../lib/random-string';
import { sleep } from '../lib/sleep';
import { AppConfigService } from '../providers/config.service';
import { AnalyticsService } from './analytics.service';
import { DataLoaderService } from './data-loader.service';
import { GetAllService } from './get-all.service';
import { SocketService } from './socket.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class TenantService {
  public tenantPlanMap: Map<string, string> = new Map();
  public DateForWorkspaceStepRemoved =
    this.config.env === 'production' ? new Date('2023-04-01').getTime() : new Date('2023-03-31').getTime();

  constructor(
    private state: StateService,
    private http: HttpClient,
    private loader: DataLoaderService,
    private config: AppConfigService,
    private storage: StorageService,
    private analyticsService: AnalyticsService,
    private socketService: SocketService,
    private auth: AuthService,
    private getAllService: GetAllService
  ) {
    this.socketService.onSocketEvent.subscribe(event => {
      if (!event) return;
      if (event.name === 'tenant.updated') {
        this.clearAsync();
        const tenant = event.payload.tenant;
        tenant['storage'] = { active: BytesStringToKb(tenant['active_storage']) };
        Object.assign(this.state.activeTenant, event.payload.tenant);
      }
    });
  }

  private clearAsync = debounce(() => {
    this.loader.remove('profile');
    this.loader.removeFuzzy('billingCustomer');
    this.loader.remove('plan-name_');
    this.loader.remove('scheduledUpdates');
  }, 1000 * 5);

  public async loadTenants(): Promise<TenantDto[]> {
    try {
      const tokenData = await this.auth.getTokenData();
      if (!tokenData) return;
      const resp = await this.getProfileData();
      const tenants = resp.tenants;
      tenants.forEach(tenant => {
        tenant['storage'] = { active: BytesStringToKb(tenant['active_storage']) };
      });

      this.state.tenants = tenants.filter(t => !t.deleted_at);
      await this.setActiveTenant(this.getDefaultTenant());
      return tenants;
    } catch (err) {
      // TODO: need to find a way to logout in this case...
      // Can't include auth service because we don't want to add auth0.js to the bundle
      // Maybe we could solve this by making sure the tenant service is not included in the bundle
    }
  }

  private getDefaultTenant(): TenantDto {
    const storageTenantId = this.storage.get('activeTenantId') || this.storage.get('backupTenantId');
    if (storageTenantId) {
      const tenant = this.state.tenants.find(t => t.id === storageTenantId);
      if (tenant) return tenant;
    }
    if (this.state.tenants.length > 1) return;
    return this.state.tenants[0];
  }

  public async setActiveTenant(tenant: TenantDto) {
    if (!tenant) {
      if (this.state.activeTenant) this.loader.clear();
      this.state.activeTenantRole = undefined;
      this.state.activeTenantPermissions = undefined;
      this.storage.remove('activeTenantId');
      this.storage.remove('backupTenantId');
      this.state.activeTenant = undefined;
      this.state.plan = undefined;
      this.state.stripeCustomer = undefined;
      this.state.stripeCustomerId = undefined;
      return;
    }
    if (this.state.activeTenant && this.state.activeTenant.id === tenant.id) {
      this.state.activeTenant = tenant; // updating reference
      return;
    }
    if (this.state.activeTenant) this.loader.clear();
    this.state.activeTenant = tenant;

    const resp = await this.getCurrentTenant();
    this.state.activeTenantRole = resp.role;
    this.state.activeTenantPermissions = resp.permissions;
    this.storage.set('activeTenantId', tenant.id, true);
    this.storage.set('backupTenantId', tenant.id);
  }

  public async refreshTenant(): Promise<void> {
    await sleep(1000); // sleeping because the updating of storage used is async
    this.loader.remove('profile');
    this.loader.remove('members');
    this.loader.remove('invites');
    await this.loadTenants();
    this.analyticsService.identify();
  }

  public async updateTenant(tenant: TenantDto, params: UpdateTenantBodyDto) {
    await lastValueFrom(this.http.patch(`${this.config.apiEndpoint}/tenants/${tenant.id}`, params));
    this.loader.remove('profile');
    await this.loadTenants();
  }

  public async joinTenant(token: string) {
    this.loader.remove('profile');
    await this.loadTenants();
    const oldTenants = this.state.tenants.slice();
    await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/invitations/${token}/accept`, {}));
    this.loader.remove('profile');
    await this.loadTenants();
    const newTenants = this.state.tenants.slice();
    const newTenant = newTenants.find(t => !oldTenants.find(old => old.id === t.id));
    await this.setActiveTenant(newTenant);
    this.loader.remove('invites');
    this.loader.remove('members');
  }

  public async inviteUser(email: string, role: 'admin' | 'member') {
    this.loader.remove('invites');
    const params = { email, role };
    return lastValueFrom(this.http.post(`${this.config.apiEndpoint}/invitations`, params));
  }

  public async getPendingInvites(): Promise<InvitationDto[]> {
    return this.loader.get('invites', () => this.getPendingInvitesInternal(), membersCacheMaxAge);
  }

  private async getPendingInvitesInternal() {
    const invites = await this.getAllService.getAll<InvitationDto>(`${this.config.apiEndpoint}/invitations`);
    return invites;
  }

  public async deleteInvite(inviteId: string) {
    this.loader.remove('invites');
    await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/invitations/${inviteId}`));
  }

  public async getMembers() {
    return this.loader.get('members', () => this.getMembersInternal(), membersCacheMaxAge);
  }

  private async getMembersInternal() {
    const members = await this.getAllService.getAll<MemberDto>(`${this.config.apiEndpoint}/members`);
    members.forEach(m => {
      m['name'] = `${m.first_name}${m.last_name ? ` ${m.last_name}` : ''}`;
    });
    const myId = this.state.user.id;
    return [
      members.find(m => m.id === myId),
      ...members.filter(m => m.role === 'owner' && m.id !== myId),
      ...members.filter(m => m.role === 'admin' && m.id !== myId),
      ...members.filter(m => m.role === 'member' && m.id !== myId),
    ];
  }

  public async updateMember(memberId: string, params: UpdateMemberBodyDto) {
    await lastValueFrom(this.http.patch(`${this.config.apiEndpoint}/members/${memberId}`, params));
    this.loader.remove('members');
  }

  public async removeMember(memberId: string) {
    await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/members/${memberId}`));
    this.loader.remove('profile');
    this.loader.remove('members');
  }

  public async getProfileData() {
    return this.loader.get('profile', () => this.getProfileDataInternal());
  }

  public async autoCreateTenant(user: UserDto, lastTriedPath?: string, count = 0) {
    if (count > 5) throw new Error('Could not create tenant');
    const path = lastTriedPath
      ? `${lastTriedPath}-${randomString(4)}`
      : user.email
          .split('@')[0]
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '');

    const name = `${this.state.user.first_name}'s Workspace`;
    const created = await this.createTenant(name, path);
    if (!created) return await this.autoCreateTenant(user, path, count + 1);
    return true;
  }

  public async createTenant(
    name = 'Unnamed tenant',
    path = randomString(16).toLowerCase(),
    otherParams: Partial<CreateTenantBodyDto> = {}
  ): Promise<boolean> {
    const params = { name, path, ...otherParams };
    try {
      await lastValueFrom(this.http.post<TenantDto>(`${this.config.apiEndpoint}/tenants`, params));
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (err.error.code === 'tenant_exists') return false;
      if (err.error.code === 'invalid_tenant_path') return false;
      throw err;
    }
    this.loader.remove('profile');
    await this.loadTenants();
    return true;
  }

  public shouldShowWorkspaceDialog(gallery: GalleryDto) {
    const tenantId = gallery['tenant']?.id || gallery['tenant'];
    const activeTenant = this.state.activeTenant;
    if (!activeTenant) return false;
    if (activeTenant.id !== tenantId) return false;
    if (this.state.activeTenantRole === 'member') return false;
    if (!activeTenant.name) return true;
    // TODO: date needs to be updated to match the date when the workspace step will be removed from the onboarding
    if (activeTenant.created_at < this.DateForWorkspaceStepRemoved / 1000) {
      // tenants created before this date didn't have the setWorkspaceName value set in the onboarding checklist
      // tenants used to be created with this name, so we use it as a flag to show the dialog
      return activeTenant.name === 'Unnamed tenant';
    }
    return !activeTenant.onboarding_checklist.setWorkspaceName;
  }

  public async deleteTenant(tenant: TenantDto) {
    await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/tenants/${tenant.id}`));
    this.loader.remove('profile');
  }

  private async getCurrentTenant() {
    return await lastValueFrom(
      this.http.get<{ role: MemberRole; permissions: string[] }>(`${this.config.apiEndpoint}/profile/current_tenant`)
    );
  }

  private async getProfileDataInternal(): Promise<{ user: UserDto; tenants: TenantDto[] }> {
    const resp = <{ tenants: { data: TenantDto[] }; user: UserDto }>(
      await lastValueFrom(this.http.get(`${this.config.apiEndpoint}/profile/current_user`))
    );
    return {
      user: resp.user,
      tenants: resp.tenants.data,
    };
  }
}
